import { isRPCRequest, isRPCResponse, type RPCMessage } from '../rpc/bridge-api-rpc-message';

/**
 * The message envelope actually exchanged between bridge API clients at a low-level between transports.
 *
 * @description Can be used for logging and filtering purposes. E.g., to filter out events from the current
 * window to itself or irrelevant to the current window in the browser implementation.
 */
export type TransportEnvelope = {
	/**
	 * Version of the bridge API being used.
	 *
	 * @description Use this if you must communicate with older versions of the bridge API. Make sure to send
	 * both your new & old message versions to maintain backwards-compatibility.
	 */
	version: 1;
	/**
	 * Source from which this envelope originated. You should ignore this message if this
	 * value aligns with {@link Transport.source}.
	 */
	source: string;
	/**
	 * RPC message to be sent over-the-wire.
	 */
	message: RPCMessage;
};

/**
 * Validates an incoming message as being from a bridge API client.
 *
 * @description NOTE: {@link window.postMessage} is often used by Chrome extensions and frontend libraries for communication.
 * Hence, it is important to ensure any message we receive via {@link window.postMessage} is validated.
 *
 * @param envelope raw envelope sent over the transport to the current JS context
 */
export const isTransportEnvelope = (envelope: TransportEnvelope) => {
	return Boolean(
		envelope &&
			typeof envelope.version === 'number' &&
			typeof envelope.source === 'string' &&
			envelope.message &&
			typeof envelope.message === 'object' &&
			(isRPCRequest(envelope.message) || isRPCResponse(envelope.message)),
	);
};
